import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { authAPI } from '../services/api';
import { User, AuthTokens, LoginCredentials, RegisterData, CustomerProfile } from '../types';

interface AuthContextType {
  user: User | null;
  profile: CustomerProfile | null;
  tokens: AuthTokens | null;
  loading: boolean;
  login: (credentials: LoginCredentials) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => void;
  updateProfile: (data: Partial<CustomerProfile> | FormData) => Promise<void>;
  isAuthenticated: boolean;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<CustomerProfile | null>(null);
  const [tokens, setTokens] = useState<AuthTokens | null>(null);
  const [loading, setLoading] = useState(true);

  // Load tokens from localStorage on mount
  useEffect(() => {
    const loadAuth = async () => {
      const storedTokens = localStorage.getItem('authTokens');
      if (storedTokens) {
        try {
          const parsedTokens = JSON.parse(storedTokens);
          setTokens(parsedTokens);

          // Set token in axios default headers
          if (parsedTokens.access) {
            localStorage.setItem('token', parsedTokens.access);
          }

          // Load user profile
          const profileData = await authAPI.getProfile();
          setProfile(profileData);
          setUser({
            id: profileData.id,
            username: profileData.username,
            email: profileData.email,
            first_name: profileData.first_name,
            last_name: profileData.last_name,
            full_name: profileData.full_name,
            is_staff: profileData.is_staff,
            is_superuser: profileData.is_superuser,
          });
        } catch (error) {
          console.error('Failed to load auth:', error);
          localStorage.removeItem('authTokens');
          localStorage.removeItem('token');
        }
      }
      setLoading(false);
    };

    loadAuth();
  }, []);

  const login = async (credentials: LoginCredentials) => {
    try {
      console.log('Attempting login with:', credentials.username);
      const tokenData = await authAPI.login(credentials);
      console.log('Login successful, received tokens:', tokenData);

      setTokens(tokenData);
      localStorage.setItem('authTokens', JSON.stringify(tokenData));

      if (tokenData.access) {
        localStorage.setItem('token', tokenData.access);
      } else {
        console.error('No access token in response:', tokenData);
      }

      // Load user profile
      const profileData = await authAPI.getProfile();
      setProfile(profileData);
      setUser({
        id: profileData.id,
        username: profileData.username,
        email: profileData.email,
        first_name: profileData.first_name,
        last_name: profileData.last_name,
        full_name: profileData.full_name,
        is_staff: profileData.is_staff,
        is_superuser: profileData.is_superuser,
      });
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  };

  const register = async (data: RegisterData) => {
    try {
      const response = await authAPI.register(data);
      setTokens(response.tokens);
      setUser(response.user);
      localStorage.setItem('authTokens', JSON.stringify(response.tokens));
      localStorage.setItem('token', response.tokens.access);

      // Load full profile
      const profileData = await authAPI.getProfile();
      setProfile(profileData);
    } catch (error) {
      console.error('Registration failed:', error);
      throw error;
    }
  };

  const logout = () => {
    if (tokens?.refresh) {
      authAPI.logout(tokens.refresh).catch(console.error);
    }
    setUser(null);
    setProfile(null);
    setTokens(null);
    localStorage.removeItem('authTokens');
    localStorage.removeItem('token');
  };

  const updateProfile = async (data: Partial<CustomerProfile> | FormData) => {
    try {
      let updatedProfile: CustomerProfile;
      if (data instanceof FormData) {
        updatedProfile = await authAPI.updateProfileWithPicture(data);
      } else {
        updatedProfile = await authAPI.updateProfile(data);
      }
      setProfile(updatedProfile);
      setUser({
        id: updatedProfile.id,
        username: updatedProfile.username,
        email: updatedProfile.email,
        first_name: updatedProfile.first_name,
        last_name: updatedProfile.last_name,
        full_name: updatedProfile.full_name,
      });
    } catch (error) {
      console.error('Profile update failed:', error);
      throw error;
    }
  };

  const value = {
    user,
    profile,
    tokens,
    loading,
    login,
    register,
    logout,
    updateProfile,
    isAuthenticated: !!user,
    isAdmin: !!(user?.is_staff || user?.is_superuser),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
